import React, { useEffect, useState, useMemo } from "react";
import Moment from "react-moment";
import { useDispatch, useSelector } from "react-redux";
import {
    getCurrentUser,
    getLoggedUserDetails,
    loadUserDetails
} from "../store/auth";
import { getPartnerDetails } from "../store/partner";
import {
    useLoadAppRuningTasksQuery,
    useLoadCandidateAppsQuery
} from "../store/candidateApiSlice";
import { getFieldData, getFormatedNumber } from "../../Services/appService";

const PARTNER_INFO_STYLES = {
    shell: {
        fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: "#ffffff",
        border: "1px solid rgba(226, 232, 240, 0.95)",
        borderRadius: "20px",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
        overflow: "hidden"
    },
    hero: {
        padding: "16px 20px",
        background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)",
        borderBottom: "1px solid rgba(219, 234, 254, 0.9)",
        display: "flex",
        alignItems: "center"
    },
    heroRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: "16px",
        flexWrap: "wrap",
        alignItems: "center",
        width: "100%"
    },
    heroCopy: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        minWidth: "220px",
        flex: 1
    },
    avatarBadge: {
        width: "44px",
        height: "44px",
        borderRadius: "12px",
        background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
        color: "#ffffff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
        fontWeight: 600,
        boxShadow: "0 8px 18px rgba(37, 99, 235, 0.2)",
        flexShrink: 0
    },
    title: {
        fontSize: "20px",
        fontWeight: 600,
        lineHeight: 1.2,
        color: "#0f172a",
        margin: 0,
        letterSpacing: "-0.01em"
    },
    subtitle: {
        fontSize: "12px",
        color: "#64748b",
        margin: 0
    },
    badgeRow: {
        display: "flex",
        gap: "10px",
        flexWrap: "wrap",
        alignItems: "center"
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "7px 14px",
        borderRadius: "8px",
        backgroundColor: "#ffffff",
        border: "1px solid rgba(203, 213, 225, 0.85)",
        fontSize: "12px",
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
        whiteSpace: "nowrap"
    },
    badgeIcon: {
        color: "#2563eb",
        fontSize: "13px"
    },
    badgeLabel: {
        color: "#64748b",
        fontWeight: 600
    },
    badgeValue: {
        color: "#1e3a8a",
        fontWeight: 600
    },
    toggleButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "7px 14px",
        borderRadius: "8px",
        backgroundColor: "#ffffff",
        border: "1px solid rgba(191, 219, 254, 0.95)",
        color: "#2563eb",
        fontSize: "12px",
        fontWeight: 600,
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
        whiteSpace: "nowrap",
        cursor: "pointer",
        outline: "none",
        transition: "all 0.2s ease"
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "14px",
        padding: "18px"
    },
    card: {
        backgroundColor: "#ffffff",
        border: "1px solid rgba(226, 232, 240, 0.95)",
        borderRadius: "16px",
        padding: "16px 18px",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.03)",
        transition: "all 0.2s ease"
    },
    cardInner: {
        display: "flex",
        alignItems: "flex-start",
        gap: "14px"
    },
    cardIcon: {
        width: "38px",
        height: "38px",
        borderRadius: "10px",
        backgroundColor: "#eff6ff",
        color: "#2563eb",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "15px",
        border: "1px solid rgba(191, 219, 254, 0.9)",
        flexShrink: 0
    },
    cardContent: {
        flex: 1,
        minWidth: 0
    },
    cardLabel: {
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#64748b",
        marginBottom: "6px"
    },
    primaryText: {
        fontSize: "15px",
        fontWeight: 600,
        lineHeight: 1.3,
        color: "#0f172a",
        marginBottom: "4px",
        wordBreak: "break-word"
    },
    secondaryText: {
        fontSize: "13px",
        lineHeight: 1.45,
        color: "#475569",
        wordBreak: "break-word"
    },
    emptyText: {
        fontSize: "13px",
        color: "#94a3b8",
        fontStyle: "italic",
        margin: 0
    }
};

export default function PartnerInfo() {
    const dispatch = useDispatch();
    const [partnerDetails, setPartnerDetails] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [wfTask, setWfTask] = useState(null);

    const userDetailsSelector = useSelector(getLoggedUserDetails);
    const partnerDetailsSelector = useSelector(getPartnerDetails);

    const currentUser = getCurrentUser();

    const usrId = currentUser?.linkUser || currentUser?.id;

    // RTK Query Hooks
    const { data: candidateAppsSelector = [] } = useLoadCandidateAppsQuery(usrId, { skip: !usrId });

    const mainWorkflow = candidateAppsSelector.filter(x => x.workflowType === 1);
    const wf = mainWorkflow[0] || null;

    const { data: appTasksSelector = [] } = useLoadAppRuningTasksQuery(
        { wfId: wf?.oldWorkflowId, userId: usrId },
        { skip: !wf?.oldWorkflowId || !usrId }
    );

    useEffect(() => {
        let targetUsrId = currentUser?.linkUser || currentUser?.id;
        if (!userDetailsSelector && targetUsrId) {
            dispatch(loadUserDetails(targetUsrId));
        }
    }, [dispatch, currentUser, userDetailsSelector]);

    useEffect(() => {
        if (appTasksSelector.length > 0) {
            const filterTasks = appTasksSelector.filter(x => x.taskType === 1 && x.wiid > 0);
            if (filterTasks.length > 0) {
                setWfTask(filterTasks[0]);
            } else {
                setWfTask(null);
            }
        } else {
            setWfTask(null);
        }
    }, [appTasksSelector]);

    useEffect(() => {
        setPartnerDetails(partnerDetailsSelector);
    }, [partnerDetailsSelector]);

    const usrType = localStorage.getItem("userType");
    const item = partnerDetails || "";

    const companyName = item !== "" ? (item.partnerName || item.label || "Partner Workspace") : "Partner Workspace";
    const firstLetter = companyName.charAt(0).toUpperCase() || "P";

    const getCompanyEmail = () => {
        if (item && item.email) return item.email;
        if (item && item.contact1 && item.contact1.performeremail) return item.contact1.performeremail;
        if (!wfTask) return "";
        if (usrType === "recruiter") {
            return getFieldData(wfTask.stateData, "recruiterenrollmentform", "Company_Email");
        }
        if (usrType === "subcontractor") {
            return getFieldData(wfTask.stateData, "subcontractorenrollmentform", "Company_Email");
        }
        return "";
    };

    const getCompanyPhone = () => {
        if (item && item.contactNo) return getFormatedNumber(item.contactNo);
        if (item && item.contact1 && item.contact1.contactNo) return getFormatedNumber(item.contact1.contactNo);
        if (!wfTask) return "";
        if (usrType === "recruiter") {
            return getFormatedNumber(getFieldData(wfTask.stateData, "recruiterenrollmentform", "Company_Phone"));
        }
        if (usrType === "subcontractor") {
            return getFormatedNumber(getFieldData(wfTask.stateData, "subcontractorenrollmentform", "Company_Phone"));
        }
        return "";
    };

    const renderAddressLines = () => {
        if (item && item.address) {
            const { address1, address2, city, state, country, zipCode } = item.address;
            const streetLine = [address1, address2].filter(Boolean).join(", ");
            const locationLine = [city, state, country].filter(Boolean).join(", ");
            const fullLocation = [locationLine, zipCode].filter(Boolean).join(" ");
            if (streetLine || fullLocation) {
                return (
                    <>
                        {streetLine && <div style={PARTNER_INFO_STYLES.secondaryText}>{streetLine}</div>}
                        {fullLocation && <div style={PARTNER_INFO_STYLES.secondaryText}>{fullLocation}</div>}
                    </>
                );
            }
        }

        if (!wfTask) {
            return <p style={PARTNER_INFO_STYLES.emptyText}>No address details on file.</p>;
        }

        let ddType = "recruiterenrollmentform";
        if (usrType === "subcontractor") ddType = "subcontractorenrollmentform";

        const lines = [
            getFieldData(wfTask.stateData, ddType, "Company_Address_Line1"),
            getFieldData(wfTask.stateData, ddType, "Company_Address_Line2")
        ].filter(Boolean);
        const city = getFieldData(wfTask.stateData, ddType, "Company_City");
        const state = getFieldData(wfTask.stateData, ddType, "Company_State");
        const zip = getFieldData(wfTask.stateData, ddType, "Company_Zip_Code");
        const country = getFieldData(wfTask.stateData, ddType, "Company_Country");
        const locationLine = [city, state, country, zip].filter(Boolean).join(", ");

        if (lines.length === 0 && !locationLine && !country) {
            return <p style={PARTNER_INFO_STYLES.emptyText}>No address on file.</p>;
        }

        return <>
            {lines.map((line, index) => (
                <div key={index} style={PARTNER_INFO_STYLES.secondaryText}>{line}</div>
            ))}
            {locationLine && <div style={PARTNER_INFO_STYLES.secondaryText}>{locationLine}</div>}
        </>;
    };

    return <React.Fragment>
        <style>{`
            .partner-info-card:hover {
                transform: translateY(-2px);
                border-color: rgba(191, 219, 254, 0.9) !important;
                box-shadow: 0 10px 25px rgba(37, 99, 235, 0.07) !important;
            }
        `}</style>
        <div style={PARTNER_INFO_STYLES.shell}>
            <div style={PARTNER_INFO_STYLES.hero}>
                <div style={PARTNER_INFO_STYLES.heroRow}>
                    <div style={PARTNER_INFO_STYLES.heroCopy}>
                        <div style={PARTNER_INFO_STYLES.avatarBadge}>
                            {firstLetter}
                        </div>
                        <div>
                            <h2 style={PARTNER_INFO_STYLES.title}>{companyName}</h2>
                            <p style={PARTNER_INFO_STYLES.subtitle}>
                                Partner Organization Profile & Key Details
                            </p>
                        </div>
                    </div>
                    <div style={PARTNER_INFO_STYLES.badgeRow}>
                        <span style={PARTNER_INFO_STYLES.badge}>
                            <i className="fa fa-id-badge" style={PARTNER_INFO_STYLES.badgeIcon} aria-hidden="true"></i>
                            <span style={PARTNER_INFO_STYLES.badgeLabel}>ID:</span>
                            <span style={PARTNER_INFO_STYLES.badgeValue}>{item !== "" ? item.fuid : "--"}</span>
                        </span>
                        <span style={PARTNER_INFO_STYLES.badge}>
                            <i className="fa fa-users" style={PARTNER_INFO_STYLES.badgeIcon} aria-hidden="true"></i>
                            <span style={PARTNER_INFO_STYLES.badgeLabel}>Total Users:</span>
                            <span style={PARTNER_INFO_STYLES.badgeValue}>{item !== "" && item.activeUsers !== undefined ? item.activeUsers : 0}</span>
                        </span>
                        <span style={PARTNER_INFO_STYLES.badge}>
                            <i className="fa fa-calendar" style={PARTNER_INFO_STYLES.badgeIcon} aria-hidden="true"></i>
                            <span style={PARTNER_INFO_STYLES.badgeLabel}>Registration:</span>
                            <span style={PARTNER_INFO_STYLES.badgeValue}>{item !== "" ? <Moment format="MMM Do YYYY">{item.createdDate}</Moment> : "--"}</span>
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={PARTNER_INFO_STYLES.toggleButton}
                            onClick={() => setShowDetails(!showDetails)}
                            title={showDetails ? "Hide Details" : "Show Details"}
                        >
                            <i className={`fa ${showDetails ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true"></i>
                            <span>{showDetails ? "Hide Details" : "Show Details"}</span>
                        </button>
                    </div>
                </div>
            </div>

            {showDetails && (
                <div style={PARTNER_INFO_STYLES.grid}>
                    <div className="partner-info-card" style={PARTNER_INFO_STYLES.card}>
                        <div style={PARTNER_INFO_STYLES.cardInner}>
                            <span style={PARTNER_INFO_STYLES.cardIcon}><i className="fa fa-building-o" aria-hidden="true"></i></span>
                            <div style={PARTNER_INFO_STYLES.cardContent}>
                                <div style={PARTNER_INFO_STYLES.cardLabel}>Company Details</div>
                                {item !== "" ? <>
                                    <div style={PARTNER_INFO_STYLES.secondaryText}>
                                        <i className="fa fa-envelope-o mr-1 text-muted"></i>
                                        {getCompanyEmail() || "No company email on file."}
                                    </div>
                                    <div style={PARTNER_INFO_STYLES.secondaryText}>
                                        <i className="fa fa-phone mr-1 text-muted"></i>
                                        {getCompanyPhone() || "No company phone on file."}
                                    </div>
                                </> : <p style={PARTNER_INFO_STYLES.emptyText}>Partner details loading...</p>}
                            </div>
                        </div>
                    </div>

                    <div className="partner-info-card" style={PARTNER_INFO_STYLES.card}>
                        <div style={PARTNER_INFO_STYLES.cardInner}>
                            <span style={PARTNER_INFO_STYLES.cardIcon}><i className="fa fa-map-marker" aria-hidden="true"></i></span>
                            <div style={PARTNER_INFO_STYLES.cardContent}>
                                <div style={PARTNER_INFO_STYLES.cardLabel}>Address</div>
                                {renderAddressLines()}
                            </div>
                        </div>
                    </div>

                    <div className="partner-info-card" style={PARTNER_INFO_STYLES.card}>
                        <div style={PARTNER_INFO_STYLES.cardInner}>
                            <span style={PARTNER_INFO_STYLES.cardIcon}><i className="fa fa-user-circle-o" aria-hidden="true"></i></span>
                            <div style={PARTNER_INFO_STYLES.cardContent}>
                                <div style={PARTNER_INFO_STYLES.cardLabel}>Key Contact</div>
                                {item !== "" && item.contact1 ? <>
                                    <div style={PARTNER_INFO_STYLES.primaryText}>
                                        {item.contact1.performername} {item.contact1.lastName}
                                    </div>
                                    <div style={PARTNER_INFO_STYLES.secondaryText}>
                                        <i className="fa fa-envelope-o mr-1 text-muted"></i>
                                        {item.contact1.performeremail || "No email on file."}
                                    </div>
                                    <div style={PARTNER_INFO_STYLES.secondaryText}>
                                        <i className="fa fa-phone mr-1 text-muted"></i>
                                        {item?.contact1?.contactNo ? getFormatedNumber(item.contact1.contactNo) : "No phone on file."}
                                    </div>
                                </> : <p style={PARTNER_INFO_STYLES.emptyText}>No primary contact assigned.</p>}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </React.Fragment>;
}
